import React from 'react';
import { DEFENSE_STEPS, GUIDE_COURSES, GUIDE_TERMS, OFFICIAL } from '../doctoralTemplate';
import { Hero } from './dp120Parts';

const TOC = [
  ['dp120-g-intro', 'Общая карта', '◉'],
  ['dp120-g-terms', 'Термины и роли', 'Aa'],
  ['dp120-g-year1', '1 курс', '01'],
  ['dp120-g-year2', '2 курс', '02'],
  ['dp120-g-year3', '3 курс', '03'],
  ['dp120-g-pubs', 'Публикации и требования', '∑'],
  ['dp120-g-defense', 'Предзащита и защита', '✓'],
  ['dp120-g-docs', 'Документы и образцы', '▣'],
  ['dp120-g-after', 'После обучения', '◆'],
];

function jump(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export const DoctoralGuideView: React.FC = () => (
  <>
    <Hero
      kicker="START HERE · DOCTORAL GUIDE"
      title="Как устроена докторантура"
      text="Единая ознакомительная страница: здесь можно последовательно прочитать весь трёхлетний маршрут, не покидая путеводитель. Рабочие действия выполняются в отдельных разделах слева."
      color="#4ce2bf"
      actions={
        <>
          <button type="button" className="dp120-btn gold" onClick={() => jump('dp120-g-intro')}>
            Начать с карты ↓
          </button>
          <button type="button" className="dp120-btn" onClick={() => jump('dp120-g-terms')}>
            Словарь терминов
          </button>
          <button type="button" className="dp120-btn violet" onClick={() => jump('dp120-g-defense')}>
            Путь защиты
          </button>
        </>
      }
    />
    <article className="dp120-doctoral-article">
      <nav className="dp120-guide-toc">
        <div>
          <small>СОДЕРЖАНИЕ</small>
          <strong>Путеводитель докторанта</strong>
        </div>
        {TOC.map(([id, label, mark]) => (
          <button key={id} type="button" onClick={() => jump(id)}>
            <b>{mark}</b>
            <span>{label}</span>
          </button>
        ))}
      </nav>
      <div className="dp120-guide-content">
        <section className="dp120-guide-chapter intro" id="dp120-g-intro" style={{ ['--chapter' as string]: '#42d8ff' }}>
          <header>
            <div className="chapter-icon">◉</div>
            <div>
              <span>8D01717 · ПРИЁМ 2025</span>
              <h3>Докторантура — не набор отдельных форм, а один путь от поступления до степени PhD</h3>
              <p>
                Программа 8D01717 «Казахский язык и литература» рассчитана на типичный срок 3 года. В течение шести
                семестров докторант проходит учебную и исследовательскую подготовку, последовательно выполняет НИРД,
                практики и научную стажировку, ведёт публикационный портфель и готовит докторскую диссертацию. Общий
                объём образовательной программы — 180 академических кредитов.
              </p>
              <p>
                Цифровой кабинет показывает весь маршрут заранее. Ознакомительный раздел ничего не отправляет и не
                меняет — он объясняет, что означает каждый этап и где он находится.
              </p>
            </div>
          </header>
          <div className="dp120-credit-strip">
            <div style={{ ['--c' as string]: '#42d8ff' }}><small>1 курс</small><b>60</b><span>кредитов</span></div>
            <div style={{ ['--c' as string]: '#4ce2bf' }}><small>2 курс</small><b>60</b><span>кредитов</span></div>
            <div style={{ ['--c' as string]: '#9b7cff' }}><small>3 курс</small><b>60</b><span>кредитов</span></div>
            <div style={{ ['--c' as string]: '#f1b93f' }}><small>Всего</small><b>180</b><span>кредитов</span></div>
          </div>
          <div className="dp120-guide-route">
            {[
              ['Поступление', 'Профиль и зачисление'],
              ['Научная траектория', 'Тема · руководитель · ИУП'],
              ['6 семестров', 'НИРД · аттестация'],
              ['Практики', '3 и 4 семестр'],
              ['Стажировка', 'Научный компонент'],
              ['Публикации', 'Готовность по правилам'],
              ['Предзащита', 'Кафедральный этап'],
              ['Защита', 'Диссертационный совет'],
              ['Завершение', 'Архив · отработка · диплом'],
            ].map(([title, note], index) => (
              <div key={title}>
                <b>{String(index + 1).padStart(2, '0')}</b>
                <span>
                  <strong>{title}</strong>
                  <small>{note}</small>
                </span>
              </div>
            ))}
          </div>
        </section>

        <section className="dp120-guide-chapter" id="dp120-g-terms" style={{ ['--chapter' as string]: '#4ce2bf' }}>
          <header>
            <div className="chapter-icon">Aa</div>
            <div>
              <span>ТЕРМИНЫ И УЧАСТНИКИ</span>
              <h3>Кто за что отвечает и что означают основные понятия</h3>
              <p>
                Advisor отвечает за организационное сопровождение, а научный руководитель — за научную работу. НИРД —
                это семестровая исследовательская работа, а предзащита — не сама защита.
              </p>
            </div>
          </header>
          <div className="dp120-definition-grid rich">
            {GUIDE_TERMS.map(([title, text, mark, color]) => (
              <div key={title} className="dp120-definition" style={{ ['--c' as string]: color }}>
                <i>{mark}</i>
                <strong>{title}</strong>
                <p>{text}</p>
              </div>
            ))}
          </div>
        </section>

        {GUIDE_COURSES.map((course) => (
          <section
            key={course.year}
            className="dp120-guide-chapter"
            id={`dp120-g-year${course.year}`}
            style={{ ['--chapter' as string]: course.color }}
          >
            <header>
              <div className="chapter-icon">{String(course.year).padStart(2, '0')}</div>
              <div>
                <span>
                  {course.year} КУРС · {course.credits}
                </span>
                <h3>
                  {course.title}: что происходит в течение года
                </h3>
                <p>{course.intro}</p>
              </div>
            </header>
            <div className="dp120-guide-sems">
              {course.semesters.map((semester, index) => {
                const [label, items] = semester;
                return (
                  <article key={label} className="dp120-guide-sem">
                    <div className="dp120-guide-sem-no">{String((course.year - 1) * 2 + index + 1).padStart(2, '0')}</div>
                    <div>
                      <span>{label}</span>
                      <h5>
                        {course.year} курс · {index === 0 ? 'первая' : 'вторая'} половина года
                      </h5>
                      <ul>
                        {items.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </article>
                );
              })}
            </div>
            <div className="dp120-guide-note">
              <b>К концу {course.year} курса</b>
              <p>{course.end}</p>
            </div>
          </section>
        ))}

        <section className="dp120-guide-chapter" id="dp120-g-pubs" style={{ ['--chapter' as string]: '#f1b93f' }}>
          <header>
            <div className="chapter-icon">∑</div>
            <div>
              <span>ПУБЛИКАЦИОННАЯ ГОТОВНОСТЬ</span>
              <h3>Публикации ведутся как единый реестр, а требования проверяются по действующей редакции</h3>
              <p>
                Одна статья не создаётся дважды только потому, что она индексируется одновременно в Scopus и Web of
                Science. В кабинете хранится одна запись с DOI, журналом, авторами, полным текстом и всеми индексами.
              </p>
            </div>
          </header>
          <div className="dp120-guide-note">
            <b>Что проверять заранее</b>
            <p>
              Корректность библиографических данных, DOI и полного текста; наличие подтверждения индексации;
              соответствие портфеля действующим требованиям; связь публикаций с НИРД и темой.
            </p>
          </div>
        </section>

        <section className="dp120-guide-chapter" id="dp120-g-defense" style={{ ['--chapter' as string]: '#9b7cff' }}>
          <header>
            <div className="chapter-icon">✓</div>
            <div>
              <span>ОТ ПРЕДЗАЩИТЫ ДО СТЕПЕНИ</span>
              <h3>Защитный маршрут начинается после научной и документальной готовности</h3>
              <p>В системе защитный путь показан как последовательность пяти этапов.</p>
            </div>
          </header>
          <div className="dp120-defense-guide">
            {DEFENSE_STEPS.map((step, index) => (
              <article key={step.title}>
                <b>{String(index + 1).padStart(2, '0')}</b>
                <div>
                  <h5>{step.title.replace(/^\d+\.\s*/, '')}</h5>
                  <p>{step.desc}</p>
                  <ul>
                    {step.docs.slice(0, 5).map((doc) => (
                      <li key={doc}>{doc}</li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
          <div className="dp120-law-note">
            <strong>Завершение обучения и защита — не одно и то же.</strong>
            <p>
              Если образовательная и научная компоненты освоены, но диссертация не защищена, программа предусматривает
              возможность защиты в последующие годы.
            </p>
          </div>
        </section>

        <section className="dp120-guide-chapter" id="dp120-g-docs" style={{ ['--chapter' as string]: '#42d8ff' }}>
          <header>
            <div className="chapter-icon">▣</div>
            <div>
              <span>ДОКУМЕНТЫ И ОБРАЗЦЫ</span>
              <h3>У каждого этапа — свой комплект</h3>
              <p>
                Рабочие шаблоны темы, консультантов, индивидуального плана, практик, стажировки, НИРД и выпускных
                документов хранятся в разделе «Документы и образцы». Загрузка выполняется в соответствующем рабочем
                разделе.
              </p>
            </div>
          </header>
          <div className="dp120-info-cards">
            <div>
              <b>Образовательная программа</b>
              <p>ОП 8D01717 определяет структуру подготовки, кредиты, практики, НИРД и итоговую аттестацию.</p>
            </div>
            <div>
              <b>Рабочие образцы Advisor</b>
              <p>Актуальные формы для темы, консультантов, ИУП, практик, стажировки и выпускного сопровождения.</p>
            </div>
            <div>
              <b>Нормативные документы</b>
              <p>Правила присуждения степеней, университетские регламенты и официальные страницы ENU.</p>
            </div>
          </div>
        </section>

        <section className="dp120-guide-chapter" id="dp120-g-after" style={{ ['--chapter' as string]: '#ff7968' }}>
          <header>
            <div className="chapter-icon">◆</div>
            <div>
              <span>ПОСЛЕ 3 КУРСА</span>
              <h3>Завершение программы, обходной лист, архивное дело, отработка и последующая защита</h3>
              <p>
                После завершения образовательной программы докторант закрывает выпускной контур: документы Офиса
                регистратора, обходной лист, личное архивное дело и — при наличии обязанности — трудоустройство.
              </p>
            </div>
          </header>
          <div className="dp120-guide-route compact">
            {[
              'Завершить 6 семестр',
              'Проверить транскрипт и приказы',
              'Собрать архивное дело',
              'Пройти обходной лист',
              'Оформить трудоустройство / основание',
              'Продолжить защитный маршрут',
              'После защиты внести решение и диплом',
            ].map((item, index) => (
              <div key={item}>
                <b>{String(index + 1).padStart(2, '0')}</b>
                <span>
                  <strong>{item}</strong>
                </span>
              </div>
            ))}
          </div>
        </section>

        <section className="dp120-guide-chapter sources" style={{ ['--chapter' as string]: '#f1b93f' }}>
          <header>
            <div className="chapter-icon">↗</div>
            <div>
              <span>ОФИЦИАЛЬНЫЕ ССЫЛКИ</span>
              <h3>Проверяйте актуальную редакцию перед критическим действием</h3>
              <p>Юридически значимые требования подтверждаются действующей редакцией официального источника.</p>
            </div>
          </header>
          <div className="dp120-source-links big">
            <a href={OFFICIAL.phd} target="_blank" rel="noopener noreferrer">Докторантура ENU ↗</a>
            <a href={OFFICIAL.practice} target="_blank" rel="noopener noreferrer">Практика ENU ↗</a>
            <a href={OFFICIAL.smart} target="_blank" rel="noopener noreferrer">Smart.ENU ↗</a>
            <a href={OFFICIAL.defense} target="_blank" rel="noopener noreferrer">Диссертационный совет ENU ↗</a>
            <a href={OFFICIAL.regulations} target="_blank" rel="noopener noreferrer">Нормативные документы ENU ↗</a>
            <a href={OFFICIAL.degree} target="_blank" rel="noopener noreferrer">Правила присуждения степеней ↗</a>
            <a href={OFFICIAL.employmentRules} target="_blank" rel="noopener noreferrer">Правила трудоустройства / отработки №403 ↗</a>
          </div>
        </section>
      </div>
    </article>
  </>
);
